/**
 * Position-sizing math — three textbook strategies, hand-rolled. No
 * new npm dependency: each formula is a closed form, and pulling a
 * package would be ceremony.
 *
 * All money values are integer cents (CLAUDE.md "money is integer
 * cents"). ATR is the raw indicator output (a price, not cents) — the
 * caller passes it through directly from the chart's indicator
 * payload.
 *
 * These functions are called from form handlers, so they validate
 * defensively at the boundary and throw with explicit messages on bad
 * input. Pure — no I/O, no global state.
 */

/** Kelly Criterion. */
export interface KellyInput {
  /** Probability of a winning trade in `[0, 1]`. */
  readonly winRate: number;
  /** Average win / average loss, must be > 0. */
  readonly winLossRatio: number;
}

/**
 * Returns a fraction of capital in `[0, 1]` to risk on each trade.
 * Negative Kelly clamps to 0 (don't take the bet). Values > 1 (which
 * imply leverage) clamp to 1 — sizing this calculator returns is
 * always cash-only.
 *
 * `f* = winRate - (1 - winRate) / winLossRatio`
 */
export function kellyFraction(input: KellyInput): number {
  if (
    !Number.isFinite(input.winRate) ||
    input.winRate < 0 ||
    input.winRate > 1
  ) {
    throw new Error(
      `kellyFraction: winRate must be in [0, 1], got ${input.winRate}`,
    );
  }
  if (!Number.isFinite(input.winLossRatio) || input.winLossRatio <= 0) {
    throw new Error(
      `kellyFraction: winLossRatio must be > 0, got ${input.winLossRatio}`,
    );
  }
  const raw = input.winRate - (1 - input.winRate) / input.winLossRatio;
  if (raw < 0) return 0;
  if (raw > 1) return 1;
  return raw;
}

/** Fixed-fractional sizing — risk a fixed % of account on stop-loss. */
export interface FixedFractionalInput {
  readonly accountValueCents: number;
  /** Fraction of account to risk on this trade, in `(0, 1]`. */
  readonly riskPercent: number;
  readonly entryPriceCents: number;
  readonly stopPriceCents: number;
}

export interface PositionSizeResult {
  /** Whole shares (floored — fractional shares aren't always supported by brokers). */
  readonly shares: number;
  /** Risk per share in cents (`abs(entry - stop)`). */
  readonly riskPerShareCents: number;
  /** Total dollar risk in cents (`shares * riskPerShareCents`). */
  readonly totalRiskCents: number;
}

export function fixedFractional(
  input: FixedFractionalInput,
): PositionSizeResult {
  if (
    !Number.isFinite(input.accountValueCents) ||
    input.accountValueCents <= 0
  ) {
    throw new Error(
      `fixedFractional: accountValueCents must be > 0, got ${input.accountValueCents}`,
    );
  }
  if (
    !Number.isFinite(input.riskPercent) ||
    input.riskPercent <= 0 ||
    input.riskPercent > 1
  ) {
    throw new Error(
      `fixedFractional: riskPercent must be in (0, 1], got ${input.riskPercent}`,
    );
  }
  if (
    !Number.isFinite(input.entryPriceCents) ||
    input.entryPriceCents <= 0
  ) {
    throw new Error(
      `fixedFractional: entryPriceCents must be > 0, got ${input.entryPriceCents}`,
    );
  }
  if (input.stopPriceCents === input.entryPriceCents) {
    throw new Error("fixedFractional: stopPriceCents must differ from entryPriceCents");
  }
  const riskPerShareCents = Math.abs(
    input.entryPriceCents - input.stopPriceCents,
  );
  const allowableRiskCents = input.accountValueCents * input.riskPercent;
  const shares = Math.floor(allowableRiskCents / riskPerShareCents);
  const totalRiskCents = Math.round(shares * riskPerShareCents);
  return { shares, riskPerShareCents, totalRiskCents };
}

/** ATR-based sizing — derive the stop from a multiple of recent ATR. */
export interface AtrPositionInput {
  readonly accountValueCents: number;
  readonly riskPercent: number;
  /** Latest ATR-14 value as a price (NOT cents — raw indicator output). */
  readonly atr: number;
  /** Stop distance = `atr * atrMultiplier`. Common values: 1.5, 2, 3. */
  readonly atrMultiplier: number;
  readonly entryPriceCents: number;
}

export function atrPositionSize(input: AtrPositionInput): PositionSizeResult {
  if (
    !Number.isFinite(input.accountValueCents) ||
    input.accountValueCents <= 0
  ) {
    throw new Error(
      `atrPositionSize: accountValueCents must be > 0, got ${input.accountValueCents}`,
    );
  }
  if (
    !Number.isFinite(input.riskPercent) ||
    input.riskPercent <= 0 ||
    input.riskPercent > 1
  ) {
    throw new Error(
      `atrPositionSize: riskPercent must be in (0, 1], got ${input.riskPercent}`,
    );
  }
  if (!Number.isFinite(input.atr) || input.atr <= 0) {
    throw new Error(`atrPositionSize: atr must be > 0, got ${input.atr}`);
  }
  if (
    !Number.isFinite(input.atrMultiplier) ||
    input.atrMultiplier <= 0
  ) {
    throw new Error(
      `atrPositionSize: atrMultiplier must be > 0, got ${input.atrMultiplier}`,
    );
  }
  if (
    !Number.isFinite(input.entryPriceCents) ||
    input.entryPriceCents <= 0
  ) {
    throw new Error(
      `atrPositionSize: entryPriceCents must be > 0, got ${input.entryPriceCents}`,
    );
  }
  const stopDistanceCents = Math.round(
    input.atr * input.atrMultiplier * 100,
  );
  if (stopDistanceCents <= 0) {
    throw new Error("atrPositionSize: stop distance rounded to zero cents");
  }
  const allowableRiskCents = input.accountValueCents * input.riskPercent;
  const shares = Math.floor(allowableRiskCents / stopDistanceCents);
  const totalRiskCents = Math.round(shares * stopDistanceCents);
  return { shares, riskPerShareCents: stopDistanceCents, totalRiskCents };
}
